import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { downloadCsv, get, qs } from '../api/client.js';
import { useAuth } from '../api/auth.js';
import { Badge, Card, Empty, Failure, Loading, Notice, Pagination } from '../components/ui.js';
import { formatDateTime } from '../lib/format.js';

interface AuditEntry {
  id: string;
  created_at: string;
  action: string;
  actor_type: string;
  actor: { id: string; email: string; name: string } | null;
  actor_label: string | null;
  target_type: string | null;
  target_id: string | null;
  scope: string | null;
  summary: string;
  request_id: string | null;
  ip: string | null;
}

interface Page<T> {
  items: T[];
  page: number;
  page_size: number;
  total: number;
  total_pages: number;
}

const SENSITIVE = /^(incident\.|deployment\.publish|device\.revoke|user\.|auth\.password)/;

export function AuditPage(): JSX.Element {
  const { can } = useAuth();
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [action, setAction] = useState('');

  const audit = useQuery({
    queryKey: ['audit', page, search, action],
    queryFn: () => get<Page<AuditEntry>>(`/audit${qs({ page, page_size: 50, q: search, action })}`),
    enabled: can('audit.read'),
  });

  if (!can('audit.read')) {
    return (
      <Card title="سجل التدقيق">
        <Notice tone="warn">ليست لديك صلاحية قراءة سجل التدقيق.</Notice>
      </Card>
    );
  }

  return (
    <Card
      title="سجل التدقيق"
      subtitle={`${audit.data?.total ?? 0} سجل`}
      actions={
        <>
          <input
            placeholder="بحث في الوصف"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
            style={{ width: 200 }}
          />
          <select
            aria-label="تصفية بالعملية"
            value={action}
            onChange={(e) => {
              setAction(e.target.value);
              setPage(1);
            }}
            style={{ width: 'auto' }}
          >
            <option value="">كل العمليات</option>
            <option value="incident.">الطوارئ</option>
            <option value="deployment.">النشر</option>
            <option value="command.">الأوامر</option>
            <option value="device.">الأجهزة</option>
            <option value="audio.">الصوتيات</option>
            <option value="schedule.">الجداول</option>
            <option value="user.">المستخدمون</option>
            <option value="auth.">الدخول</option>
          </select>
          <button onClick={() => void downloadCsv(`/audit${qs({ q: search, action, format: 'csv' })}`, 'audit.csv')}>
            تصدير CSV
          </button>
        </>
      }
    >
      <Notice tone="info">
        يُسجَّل النشر والتشغيل وتفعيل الإنذار وإلغاؤه وتغييرات الأدوار. لا تُسجَّل كلمات المرور ولا
        المفاتيح ولا كلمات شبكة Wi-Fi. تصدير CSV محمي من حقن الصيغ.
      </Notice>

      {audit.isLoading ? (
        <Loading />
      ) : audit.error ? (
        <Failure error={audit.error} onRetry={() => void audit.refetch()} />
      ) : audit.data!.items.length === 0 ? (
        <Empty title="لا توجد سجلات مطابقة" />
      ) : (
        <>
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>الوقت</th>
                  <th>العملية</th>
                  <th>المنفّذ</th>
                  <th>الوصف</th>
                  <th>النطاق</th>
                  <th>معرّف الطلب</th>
                </tr>
              </thead>
              <tbody>
                {audit.data!.items.map((e) => (
                  <tr key={e.id}>
                    <td style={{ fontSize: 12.5, whiteSpace: 'nowrap' }}>{formatDateTime(e.created_at)}</td>
                    <td>
                      <Badge tone={SENSITIVE.test(e.action) ? 'danger' : 'muted'}>
                        <span className="mono">{e.action}</span>
                      </Badge>
                    </td>
                    <td style={{ fontSize: 12.5 }}>
                      {e.actor?.email ?? e.actor_label ?? e.actor_type}
                      {e.ip ? (
                        <div className="mono" style={{ color: 'var(--muted)', fontSize: 11 }}>
                          {e.ip}
                        </div>
                      ) : null}
                    </td>
                    <td style={{ fontSize: 13 }}>{e.summary}</td>
                    <td style={{ fontSize: 12.5, color: 'var(--muted)' }}>{e.scope ?? '—'}</td>
                    <td className="mono" style={{ fontSize: 11, color: 'var(--muted)' }}>
                      {e.request_id?.slice(0, 8) ?? '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Pagination
            page={audit.data!.page}
            totalPages={audit.data!.total_pages}
            total={audit.data!.total}
            onChange={setPage}
          />
        </>
      )}
    </Card>
  );
}
